import { all, call, put, select } from 'redux-saga/effects';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { fetchContentForInitialSlice, getFavoritesResourceQueryForContentTypes, getRecentsResourceQueryForContentTypes, getRecommendationsResourceQuery, getSharedWithMeResourceQuery } from '~src/spacesPlatform/sagas/content/MixedContentSagas';
import { getRecentContentTypes, selectFavoriteContentTypes, selectSharedWithMeContentTypes } from '~src/spacesPlatform/selectors/ContentFilterSelectors';

import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { SessionSiteSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../codegen/Localize';
import { fetchDraftsRequested } from '../../drafts/actions/DraftsActions';
import { favoritesSpaceDefaultSortOrder } from '../../favorites/models/FavoritesSortOptions';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { recentsSpaceDefaultSortOrder } from '../../recents/models/RecentsSortOptions';
import { ISharedWithMeContentInfo } from '../../sharing/models/ISharedWithMeContentInfo';
import { sharedWithMeDefaultSortOrder } from '../../sharing/models/sort/SharedWithMeSortOptions';
import { getExplorableItemDefinition } from '../../spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '../../spacesPlatform/models/ContentType';
import { IContentInfo } from '../../spacesPlatform/models/IContentInfo';
import { OrderKeys } from '../../spacesPlatform/models/OrderKeys';
import { getHomeFetchFavoritesSucceededAction, getHomeFetchPopularSucceededAction, getHomeFetchRecentsSucceededAction, getHomeFetchRecommendationsSucceededAction, getHomeFetchSharedWithMeSucceededAction } from '../actions/HomeActions';
import { ChannelTypes } from '../types/ChannelTypes';
import { apiPerformanceTimer, HomeApiResult } from '../utils/ApiPerformanceTimer';
import { generateUuid } from '../utils/uuidGenerator';
import { fetchServerWelcomeChannelInfo } from './HomeSaga';

// Limits the number of metric data fetched in each section of the home page, the fetch gets called on the home page 3 times once for each section.
export const INITIAL_METRIC_FETCH_SIZE = 30;

/**
 * The main function to get all the content for channels on Home, used in MixedContentSagas.ts
 */
export function* fetchHomeContent() {
  try {
    const isVizRecommendationsEnabled = yield select(SessionSiteSelectors.isVizRecommendationsEnabled);
    const isNamedSharingEnabled = yield select(SessionSiteSelectors.isNamedSharingEnabled);
    const pageViewId = generateUuid();

    const callFetchHomeSharedWithMe = isNamedSharingEnabled ? [call(apiPerformanceTimer, fetchHomeSharedWithMe, 'home', ChannelTypes.sharedWithMe, pageViewId)] : [];
    yield all([
      call(apiPerformanceTimer, fetchHomeFavorites, 'home', ChannelTypes.favorites, pageViewId),
      call(apiPerformanceTimer, fetchHomeRecents, 'home', ChannelTypes.recents, pageViewId),
      call(fetchServerWelcomeChannelInfo),
      isVizRecommendationsEnabled ?
        call(apiPerformanceTimer, fetchHomeRecommendations, 'home', ChannelTypes.recommendations, pageViewId) :
        call(apiPerformanceTimer, fetchHomePopular, 'home', ChannelTypes.popular, pageViewId),
    ].concat(callFetchHomeSharedWithMe));
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  } finally {
    yield put(fetchDraftsRequested({ spaceType: 'home' } as any));
  }
}

export function* fetchHomeFavorites(result: HomeApiResult) {
  const favoriteContentTypes: ReadonlyArray<ExploreContentApi_ContentType> = yield select(selectFavoriteContentTypes);
  const favoritesResource = yield call(getFavoritesResourceQueryForContentTypes, favoriteContentTypes, favoritesSpaceDefaultSortOrder);
  const favoritesContent: ListResult<IContentInfo> = yield call(fetchContentForInitialSlice, favoritesResource);
  yield put(getHomeFetchFavoritesSucceededAction(favoritesContent));

  result.contentLength = getContentItemLength(favoritesContent);
}

export function* fetchHomeRecents(result: HomeApiResult) {
  const contentTypes: ReadonlyArray<ExplorableItemType> = getRecentContentTypes(FeatureFlags.flowsInRecentsEnabled);
  const recentsResource = yield call(getRecentsResourceQueryForContentTypes, contentTypes, recentsSpaceDefaultSortOrder);
  const recentsContent: ListResult<IContentInfo> = yield call(fetchContentForInitialSlice, recentsResource);
  yield put(getHomeFetchRecentsSucceededAction(recentsContent));

  result.contentLength = getContentItemLength(recentsContent);
}

export function* fetchHomePopular(result: HomeApiResult) {
  const popularResource = yield call(getPopularViewsResourceQuery);
  const popularContent: ListResult<IContentInfo> = yield call(fetchContentForInitialSlice, popularResource);
  yield put(getHomeFetchPopularSucceededAction(popularContent));

  result.contentLength = getContentItemLength(popularContent);
}

export function* fetchHomeRecommendations(result: HomeApiResult) {
  const recommendationsResource = yield call(getRecommendationsResourceQuery);
  const recommendationsContent: ListResult<IContentInfo> = yield call(fetchContentForInitialSlice, recommendationsResource);
  yield put(getHomeFetchRecommendationsSucceededAction(recommendationsContent));
  result.contentLength = getContentItemLength(recommendationsContent);
}

export function* fetchHomeSharedWithMe(result: HomeApiResult) {
  const swmContentTypes: ReadonlyArray<ExploreContentApi_ContentType> =
    yield select(selectSharedWithMeContentTypes);
  const swmSpaceDefaultSortOrder = sharedWithMeDefaultSortOrder;

  const swmResource = yield call(getSharedWithMeResourceQuery, swmContentTypes, swmSpaceDefaultSortOrder);
  const swmContent = yield call(fetchContentForInitialSlice, swmResource);

  yield put(getHomeFetchSharedWithMeSucceededAction(swmContent));

  const allowedContent = new ListResult(filterSharedWithMeAccessDenied(swmContent.items));

  result.contentLength = getContentItemLength(swmContent);
}

function* getPopularViewsResourceQuery() {
  try {
    const resource = getExplorableItemDefinition('view').getBasicQuery()
      .withOrder([Order.desc(OrderKeys.hitsLastOneMonthTotal)]);
    return resource;
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

function getContentItemLength(content: ListResult<IContentInfo>) {
  return (content && content.items && content.items.length) || 0;
}

function filterSharedWithMeAccessDenied(channelContent: IContentInfo<ExplorableItemType>[]) {
  return channelContent.filter((content) => {
    return !(content as ISharedWithMeContentInfo).isAccessDenied;
  });
}